import * as bitcoin from 'bitcoinjs-lib';
import * as bitcoinMessage from 'bitcoinjs-message';
import * as bip39 from 'bip39';
import BIP32Factory from 'bip32';
import ECPairFactory from 'ecpair';
import ecc from '@bitcoinerlab/secp256k1';
import { words } from './globalState';
import { encrypt, decrypt } from './crypto';
import { handleClickScanQr, readNfc, decodeNdefRecord, isIPhone } from './utils';
import { devLog, wrapErrorWithCause, notificationMessageWithError } from './utils';

const bip32 = BIP32Factory(ecc);
const ECPair = ECPairFactory(ecc);
// GENERATE MNEMONIC
function generateMnemonic ({ dispatch, mnemonicArg, network, language }) {
  let mnemonic;
  let keyPairs;
  let address;

  try {
    if (mnemonicArg === undefined || mnemonicArg === null || mnemonicArg === '') {
      mnemonic = bip39.generateMnemonic(256);
    }
    else {
      mnemonic = mnemonicArg;
    }

    keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language });
    address = getAddressFromKeyNW({ dispatch, keyPair: keyPairs[0], language, network });
  }
  catch (error) {
    const message = notificationMessageWithError(words.failedToGenerateMnemonic[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToGenerateMnemonic', error);
  }

  return {
    mnemonic: mnemonic,
    keyPairs: keyPairs,
    address: address,
  }
}

// WIFからキーペア・アドレス取得
function getKeyPairAndAddressFromWif({ dispatch, wif, network, language }) {
  let keyPair;
  let address;

  try {
    if (typeof wif !== 'string' || wif === '') {
      throw new Error('wif is missing');
    }

    keyPair = ECPair.fromWIF(wif, network);
    address = getAddressFromKeyNW({ dispatch, keyPair, language, network });
  }
  catch (error) {
    devLog('getKeyPairAndAddressFromWif ; error', error);
    const message = notificationMessageWithError(words.failedToGetKeyPairFromWif[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToGetKeyPairFromWif', error);
  }

  return {
    keyPair,
    keyPairs: [ keyPair ],
    address,
  };
}

// mnemonicから秘密鍵取得 GET KEYPAIRS FROM MNEMONIC
function getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language, paths = ["m/44'/22'/0'/0/0"] }) {
  let keyPairs = [];

  try {
    const seed = bip39.mnemonicToSeedSync(mnemonic);
    const root = bip32.fromSeed(seed)

    for (const path of paths) {
      // const path = "m/44'/22'/0'/0/0";
      const leaf = root.derivePath(path);
      const keyPair = ECPair.fromPrivateKey(leaf.privateKey, { network });
      keyPairs.push(keyPair);
    }
  }
  catch (error) {
    devLog('getKeyPairsFromMnemonic ; error', error);
    const message = notificationMessageWithError(words.failedToGetKeyPairsFromMnemonic[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToGetKeyPairsFromMnemonic', error);
  }

  return keyPairs;
}

// アドレス抽出 GET ADDRESS FROM KEY NW
function getAddressFromKeyNW ({ dispatch, keyPair, language = 'english', network }) {
  // アドレス導出
  let payment;
  let address;

  try {
    payment = bitcoin.payments.p2pkh({ pubkey: keyPair.publicKey, network });
    address = payment.address;

    return address;
  }
  catch (err) {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[language] });
    throw err;
  }
}

// アドレス抽出 GET ADDRESS FROM KEY
function getAddressFromKey (state, dispatch, keyPair, network) {
  // アドレス導出
  let payment;
  let address;

  try {
    payment = bitcoin.payments.p2pkh({ pubkey: keyPair.publicKey, network });
    address = payment.address;

    return {
      status: 'fulfilled',
      address: address,
    };
  }
  catch (err) {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[state.language] });

    return {
      status: 'rejected',
    };
  }
}

// 鍵渡して署名 SIGN WITH KEY NW
function signWithKeyNW({ dispatch, keyPair, message, network, language }) {
  try {
    let randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);
    return bitcoinMessage.sign(message, keyPair.privateKey, keyPair.compressed, network.messagePrefix, { extraEntropy: Buffer.from(randomBytes) }).toString('base64');
  }
  catch (error) {
    devLog('signWithKeyNW ; error', error);
    const message = notificationMessageWithError(words.failedToSign[language], error);
    dispatch({ type: 'setNotification', key: 'notification', value: message });
    throw wrapErrorWithCause('failedToSign', error);   
  }
}

// 鍵渡して署名 SIGN WITH KEY
function signWithKey(state, dispatch, keyPair, message, network) {
  let signatureMona;

  try {
    let randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);
    signatureMona = bitcoinMessage.sign(message, keyPair.privateKey, keyPair.compressed, network.messagePrefix, { extraEntropy: Buffer.from(randomBytes) }).toString('base64');

    return {
      status: 'fulfilled',
      signature: signatureMona,
    };
  }
  catch (err) {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });

    return {
      status: 'rejected'
    };
  }

}

// KEEP KEY PAIRS IN MEMORY
function keepKeyPairsInMemory({ dispatch, keyPairs, periodToKeepKeyPairs, language, network }) {
  const now = new Date();

  for (const keyPair of keyPairs) {
    // アドレス導出
    let payment;
    let address;

    try {
      payment = bitcoin.payments.p2pkh({ pubkey: keyPair.publicKey, network });
      address = payment.address;
    }
    catch (err) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[language] });
      continue;
    }

    devLog('periodToKeepKeyPairs', periodToKeepKeyPairs);
    const keyPairData = {
      keyPair: keyPair,
      expiration: now.getTime() + periodToKeepKeyPairs,
    };

    dispatch({ type: 'setStateMultiLayers', keys: ['keyPairs', address], value: keyPairData });
  }
}

// GET VALID KEY PAIRS
function getValidKeyPairs({ dispatch, keyPairs }) {
  const validKeyPairs = {};
  const now = new Date();

  for (const address of Object.keys(keyPairs)) {
    if (keyPairs[address].expiration > now.getTime()) {
      validKeyPairs[address] = keyPairs[address];
    }
  }

  dispatch({ type: 'setState', key: 'keyPairs', value: validKeyPairs });

  return validKeyPairs;
}

// 鍵渡してcertificationつくる CERTIFICATION WITH KEY
// state.login.addressMain は廃止予定。 → login 以外の用途もあることを考慮せよ。
// mode は allKeys のみにする予定。つまり、作り直し。 → allKeys, matchedKey それぞれ別関数に分離する。
function certificationWithKey({ dispatch, keyPairs, messageType, stateFixed, certifications, clientTime, extendedCertificationProperties, signingMode, network, language }) {
  let addressIsSpecified;

  // lastEvaluatedSortKey等があるということは、前回の検索時からaddressMainが変更されていないということ。(変更するとリセットされてしまう。)
  // addressMainが指定されていないということは、後ページ検索ではない。
  // なぜならば、addressMainが指定されていない状態にするには、前回の検索のあと、addressMainを削除しなければならないから。
    
  if (stateFixed.addressMain !== undefined && stateFixed.addressMain !== null && stateFixed.addressMain !== '') {
    addressIsSpecified = true;
  }
  else {
    addressIsSpecified = false;
  }

  if (signingMode === 'firstKey') {
    keyPairs = [ keyPairs[0] ];
  }
  else { // matchedKey, allKeys
  }

  for (const keyPair of keyPairs) {
    // アドレス導出
    let payment;
    let address;
    let addressActual;

    try {
      payment = bitcoin.payments.p2pkh({ pubkey: keyPair.publicKey, network });
      addressActual = payment.address;
    }
    catch (err) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[language] });
      continue;
    }

    if (signingMode === 'matchedKey') {
      if (addressActual !== stateFixed.addressMain) {
        continue;
      }
      else {
        address = stateFixed.addressMain;
      }
    }
    else if (signingMode === 'firstKey') {
      if (addressIsSpecified) {
        if (checkIfAddressIsMatchedWithAnAddress({ dispatch, addressActual, address: stateFixed.addressMain, addressCheck: stateFixed.addressCheck, language })) {
          address = stateFixed.addressMain;
        }
        else {
          continue;
        }
      }
      else {
        address = addressActual;
        stateFixed.addressMain = addressActual;
      }
    }
    else { // allKeys
      address = addressActual;
    }

    // 渡された鍵ペアに対応するアドレスがcertificationsに含まれていなければ、署名する。
    if (address !== undefined && address !== null && address !== '' && !certifications.map( certification => certification.address ).includes(address)) {
      let message;

      if (messageType === 'login') {
        message = `I want to log in to monashell as ${address}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
      }
      else if (messageType === 'information') {
        message = `I want to get the information of ${address}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
      }

      devLog('message', message);

      let signatureMona;

      try {
        signatureMona = signWithKeyNW({ dispatch, keyPair, message, network, language });
      }
      catch (error) {
        dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[language] });
        continue;
      }

      const certification = {
        // addressCoinType: 'mona',
        address: address,
        addressActual: addressActual,
        clientTime: clientTime,
        signatureVersion: stateFixed.signatureVersion,
        signature: signatureMona,
        lastEvaluatedKey: stateFixed.lastEvaluatedKey,
      };

      // 拡張プロパティ
      if (extendedCertificationProperties !== undefined && extendedCertificationProperties !== null) {
        for (const property of extendedCertificationProperties) {
          certification[property.key] = property.value;
        }
      }

      devLog('certification given key', JSON.stringify(certification));

      certifications.push(certification);
    }
  }
}

// CHECK IF ADDRESS IS MATCHED WITH AN ADDRESS
function checkIfAddressIsMatchedWithAnAddress({ dispatch, addressActual, address, addressCheck, language }) {
  if (addressCheck === 'strict' || addressCheck === 'addressSecond') {
    if (addressActual !== address) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressFilledAndTheAddressInWalletAreDifferent[language] });
      return false;
    }
    else {
      return true;
    }
  }
  else { // off
    return true;
  }
}

// SET SIGNATURE BY MAIN ADDRESS
// state.login.addressMain は廃止予定。
// 作り直し予定。
async function setSignatureByAddressMain({ dispatch, stateFixed, certifications, clientTime, extendedCertificationProperties, messageType, language }) {
  // アクティブなMONAアドレスが有り、そのアドレスがcertificationsに含まれていなければ、署名する。

  // -- Mpurseのアドレス取得
  await window.mpurse.getAddress()
  .then( async (result) => {
    const addressActual = result;
    stateFixed.addressMpurse = addressActual;
    let address;

    // lastEvaluatedSortKey等があるということは、前回の検索時からaddressMainが変更されていないということ。(変更するとリセットされてしまう。)
    // addressMainが指定されていないということは、後ページ検索ではない。
    // なぜならば、addressMainが指定されていない状態にするには、前回の検索のあと、addressMainを削除しなければならないから。

    if (stateFixed.addressMain !== undefined && stateFixed.addressMain !== null && stateFixed.addressMain !== '') {
      if (stateFixed.addressCheck === 'strict' || stateFixed.addressCheck === 'addressSecond') {
        if (addressActual !== stateFixed.addressMain) {
          dispatch({ type: 'setNotification', key: 'notification', value: words.mainAddressAndTheAddressInWalletAreDifferent[language] });
          return 'rejected';
        }
        else {
          // 続行
        }
      }
      else { // off
        // 続行
      }

      // address = stateFixed.addressMain;
      address = addressActual;
    }
    else {
      address = addressActual;
      stateFixed.addressMain = addressActual;
    }

    devLog('address', address);
    devLog('certifications', JSON.stringify(certifications));

    if (!certifications.map( certification => certification.address ).includes(address)) {
      let message;

      if (messageType === 'login') {
        message = `I want to log in to monashell as ${address}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
      }
      else if (messageType === 'information') {
        message = `I want to get the information of ${address}. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
      }

      devLog('message', message);

      let signatureMona;

      try {
        signatureMona = await window.mpurse.signMessage(message).then( result => result );

        const certification = {
          // addressCoinType: 'mona',
          address: address,
          addressActual: addressActual,
          clientTime: clientTime,
          signatureVersion: stateFixed.signatureVersion,
          signature: signatureMona,
          lastEvaluatedKey: stateFixed.lastEvaluatedKey,
        };

        // 拡張プロパティ
        if (extendedCertificationProperties !== undefined && extendedCertificationProperties !== null) {
          for (const property of extendedCertificationProperties) {
            certification[property.key] = property.value;
          }
        }

        devLog('certification addressMain', JSON.stringify(certification));

        certifications.push(certification);
      }
      catch (err) {
        dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[language] });
      }
    }
  })
  .catch( (error) => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressInYourWalletCanNotBeGotten[language] });
  });
}

// HANDLE CLICK MPURSE
// deprecated
async function handleClickMpurse(state, dispatch, stateKeys, popupLayer) {
  const popupLayerNext = popupLayer !== undefined && popupLayer !== null ? (popupLayer + 1) : 0;
  devLog('handleClickMpurse', JSON.stringify(stateKeys));

  const callback = (keyPairs) => {
    const keyPair = keyPairs[0];
    const result = getAddressFromKey(state, dispatch, keyPair, state.networkMona);

    if (result.status === 'fulfilled') {
      const address= result.address;
      dispatch({ type: 'setStateMultiLayers', keys: stateKeys, value: address });
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[state.language] });
      return { status: 'rejected'};
    }

    // keepKeyPairsInMemory(state, dispatch, keyPairs);
  };

  if (state.walletType === 'mpurse') {
    if (window.mpurse) {
      const result = await window.mpurse.getAddress()
      .then( (result) => {
        dispatch({ type: 'setStateMultiLayers', keys: stateKeys, value: result });

        return {
          status: 'fulfilled',
          body: result,
        };
      })
      .catch( (error) => {
        dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressInYourWalletCanNotBeGotten[state.language] });

        return {
          status: 'rejected',
        };
      });

      return result;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.activateMpurse[state.language] });

      return {
        status: 'rejected',
      };
    }
  }
  else if (state.walletType === 'nfc') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[state.language] });
    handleClickNfc(state, dispatch, callback);
  }
  else { // qr
    dispatch({ type: 'setNotification', key: 'notification', value: words.scanYour2DBarcode[state.language] });
    handleClickScanQr(state, dispatch, callback, { popupLayer: popupLayerNext });
  }
}

// NFCボタン押下 HANDLE CLICK NFC
async function handleClickNfc(state, dispatch, callback, { args = ['keyPairs'], recordNos = [1] } = {}) {
  if ('NDEFReader' in window) {
    const ndef = new window.NDEFReader();
    await ndef.scan();

    ndef.onreading = async event => {
      console.log("NDEF tag detected");

      const keyPairs = [];
      const mnemonics = [];

      const argumentsAll = { keyPairs, mnemonics };
      const argumentsToPass = args.map( arg => argumentsAll[arg] );

      for (const [index, record] of event.message.records.entries()) {
        if (!recordNos.includes(index)) {
          continue;
        }

        try {
          const textDecoder = new TextDecoder(record.encoding);
          const mnemonicEncrypted = textDecoder.decode(record.data);
          const pass = state.config.clientParameters.specialMessage;
          const salt = ['00', '00', '00', '00', '00', '00', '00', '72'];
          const iv = ['00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '01', 'bf', '52'];
          const mnemonic = await decrypt(state, dispatch, mnemonicEncrypted, pass, salt, iv);
          mnemonics.push(mnemonic);
          const keyPair = getKeyPairsFromMnemonic({ dispatch, network: state.networkMona, mnemonic, language: state.language })[0];
          keyPairs.push(keyPair);
        }
        catch (error) {
          dispatch({ type: 'setNotification', key: 'notification', value: words.cannotReadDataFromTheKeyCard[state.language] });
        }
      }

      // 先に後処理やっちゃう。
      ndef.onreading = null;
      ndef.onreadingerror = null;

      if (argumentsToPass.some( arg => arg.length === 0 )) {
        dispatch({ type: 'setNotification', key: 'notification', value: words.cannotReadDataFromTheKeyCard[state.language] });
        throw new Error('noArgumentToPass');
      }

      callback(...argumentsToPass);
    };

    ndef.onreadingerror = () => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.cannotReadDataFromTheKeyCard[state.language] });

      ndef.onreading = null;
      ndef.onreadingerror = null;
    };
  }
  else {
    dispatch({ type: 'setNotification', key: 'notification', value: words.yourDeviceDoesNotSupportKeyCards[state.language] });
  }
}

// DO SOMETHING WITH ENCRYPTED MNEMONICS
async function doSomethingWithEncryptedMnemonics(state, dispatch, mnemonicsEncrypted, callback, { args = ['keyPairs'], recordNos = [0] } = {}) {
  const keyPairs = [];
  const mnemonics = [];

  const argumentsAll = { keyPairs, mnemonics };
  const argumentsToPass = args.map( arg => argumentsAll[arg] );

  for (const recordNo of recordNos) {
    try {
      const pass = state.config.clientParameters.specialMessage;
      const salt = ['00', '00', '00', '00', '00', '00', '00', '72'];
      const iv = ['00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '01', 'bf', '52'];
      const mnemonic = await decrypt(state, dispatch, mnemonicsEncrypted[recordNo], pass, salt, iv);
      mnemonics.push(mnemonic);
      const keyPair = getKeyPairsFromMnemonic({ dispatch, network: state.networkMona, mnemonic, language: state.language })[0];
      keyPairs.push(keyPair);
    }
    catch (error) {
      dispatch({ type: 'setNotification', key: 'notification', value: words.cannotReadDataFromTheKeyCard[state.language] });
    }
  }

  if (argumentsToPass.some( arg => arg.length === 0 )) {
    dispatch({ type: 'setNotification', key: 'notification', value: words.cannotReadDataFromTheKeyCard[state.language] });
    throw new Error('noArgumentToPass');
  }

  callback(...argumentsToPass);
}

// セッション有無識別スタイル
function indicateSessions({ session }) {
  let loginStyle;

  if (Object.values(session).some( session => session.expirationOfSession > Date.now() )) {
    loginStyle = 'backgroundColorMonacottoPale';
  }
  else {
    loginStyle = 'backgroundColorMonacottoAlmostWhite';
  }

  return loginStyle;
}

// 使用可能ウォレット
function walletsAvailable({ hasCamera }) {
  // const hasCamera = hasCameraOpt !== undefined && hasCameraOpt !== null ? hasCameraOpt : state?.device?.hasCamera;
  let walletsAvailable =[];

  if (
    typeof window.PublicKeyCredential !== "undefined" &&
    window.isSecureContext === true &&
    Boolean(navigator.credentials)
  ) {
    walletsAvailable.push('keybox');
  }

  if (window.mpurse !== undefined) {
    walletsAvailable.push('mpurse');
  }

  if ('NDEFReader' in window) {
    walletsAvailable.push('nfc');
  }

  if (isIPhone()) {
    walletsAvailable.push('nfcNative');
  }

  if (hasCamera) {
    walletsAvailable.push('qr');
  }

  return walletsAvailable;
}

// ENCRYPT BY KEY CARD STYLE
async function encryptByKeyCardStyle({ pass, dispatch, plaintext, language }) {
  const salt = ['00', '00', '00', '00', '00', '00', '00', '72'];
  const iv = ['00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '01', 'bf', '52'];
  const encrypted = await encrypt(null, dispatch, plaintext, pass, salt, iv, { language });
  return encrypted;
}

// DECRYPT BY KEY CARD STYLE
async function decryptByKeyCardStyle({ pass, dispatch, encrypted, language }) {
  const salt = ['00', '00', '00', '00', '00', '00', '00', '72'];
  const iv = ['00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '00', '01', 'bf', '52'];
  const plaintext = await decrypt(null, dispatch, encrypted, pass, salt, iv, { language });
  return plaintext;
}

// FORMAT TO KEY CARD STYLE
function formatToKeyCardStyle({ data }) {
  return [
    {
      recordType: "url",
      data: "https://knightsofmonadom.monatoka.com/?wallettype=nfc"
    },
    {
      recordType: "text",
      lang: "en",
      encoding: "utf-8",
      data,
    },
  ];
}

// FORMAT TO SINGLE TEXT RECORD
function formatToSingleTextRecord({ data }) {
  return [
    {
      recordType: "text",
      lang: "en",
      encoding: "utf-8",
      data,
    },
  ];
}

// KEEP SUB ADDRESSES IN MEMORY
// サブアドレスを上書きします。（追加ではない。）
function keepSubAddressesInMemory({ dispatch, addressMain, subAddresses }) {
  dispatch({ type: 'setStateMultiLayers', keys: ['subAddresses', addressMain, 'subAddresses'], value: subAddresses });
}

// GET ACTUAL SUB ADDRESSES
function getActualSubAddresses({ subAddresses, addressMain }) {
  const addresses = subAddresses[addressMain]?.subAddresses;

  if (!Array.isArray(addresses)) {
    return [];
  }

  // 既存データではマスター自身を含むため、展開対象から除外する
  return addresses.filter( (address) => address !== addressMain );
}

// KEEP KEY PAIRS AND SUB ADDRESSES IN MEMORY
function keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs, language, network }) {
  const keyPairsToKeep = [];
  const subAddresses = {};
  for (const mnemonicRecord of mnemonicRecords) {
    const keyPairsBelongingToAMainAddress = getKeyPairsFromMnemonic({ dispatch, network, mnemonic: mnemonicRecord.mnemonic, language, paths });
    keyPairsToKeep.push(...keyPairsBelongingToAMainAddress);
    subAddresses[mnemonicRecord.address] = keyPairsBelongingToAMainAddress.map( ( keyPair ) => getAddressFromKeyNW({ dispatch, keyPair, language, network }) );
  }

  keepKeyPairsInMemory({ dispatch, keyPairs: keyPairsToKeep, periodToKeepKeyPairs, language, network });
  
  for (const addressMain of Object.keys(subAddresses)) {
    keepSubAddressesInMemory({ dispatch, addressMain, subAddresses: subAddresses[addressMain] });
  }
}

// GET PARENT ADDRESS FROM SUB ADDRESS
function getParentAddressFromSubAddress({ subAddresses, subAddress }) {
  if (subAddresses === undefined || subAddresses === null || subAddress === undefined || subAddress === null || subAddress === '') {
    return null;
  }

  for (const addressMaster of Object.keys(subAddresses)) {
    if (Array.isArray(subAddresses[addressMaster]?.subAddresses) && subAddresses[addressMaster].subAddresses.includes(subAddress)) {
      return addressMaster;
    }
  }

  return null;
}

// GET SECRETS FROM NFC
async function getSecretsFromNfc({ dispatch, pass, language, network }) {
  const records = await new Promise( (resolve, reject) => {
    readNfc({ dispatch, resolve, reject, language });
  });
  const recordNo = 1;
  const record = records?.[recordNo];
  const encryptedMnemonic = decodeNdefRecord({ dispatch, record, language });
  const mnemonic = await decryptByKeyCardStyle({ pass, dispatch, encrypted: encryptedMnemonic, language });
  const keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language });
  const addressMain = getAddressFromKeyNW({ dispatch, keyPair: keyPairs[0], language, network });
  return { mnemonic, keyPairs, addressMain };
}

// GET SECRETS FROM NFC NATIVE KEY CARD
// ネイティブアプリが返した NDEF レコードから、キーカード形式（2件目）のニーモニックを復号する
async function getSecretsFromNfcNativeKeyCard({ dispatch, pass, records, language, network }) {
  const recordNo = 1;
  const record = records?.[recordNo];

  if (record?.kind !== 'text') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToReadNfc[language] });
    throw new Error('failedToReadNfc');
  }

  const mnemonic = await decryptByKeyCardStyle({ pass, dispatch, encrypted: record.value, language });
  const keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language });
  const addressMain = getAddressFromKeyNW({ dispatch, keyPair: keyPairs[0], language, network });
  return { mnemonic, keyPairs, addressMain };
}

// GET SECRETS FROM QR
async function getSecretsFromQr({ dispatch, pass, popupLayer, language, network }) {
  const popupLayerNext = popupLayer !== undefined && popupLayer !== null ? (popupLayer + 1) : 0;
  const encryptedMnemonic = await new Promise( (resolve, reject) => {
    const popup = {
      type: 'qrCodeReaderPopup',
      body: {
        resolve,
        reject,
      },
    };

    dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
  });
  const mnemonic = await decryptByKeyCardStyle({ pass, dispatch, encrypted: encryptedMnemonic, language });
  const keyPairs = getKeyPairsFromMnemonic({ dispatch, network, mnemonic, language });
  const addressMain = getAddressFromKeyNW({ dispatch, keyPair: keyPairs[0], language, network });
  return { mnemonic, keyPairs, addressMain };
}

export {
  generateMnemonic,
  getKeyPairAndAddressFromWif,
  getKeyPairsFromMnemonic,
  getAddressFromKeyNW,
  getAddressFromKey,
  signWithKey,
  signWithKeyNW,
  keepKeyPairsInMemory,
  getValidKeyPairs,
  certificationWithKey,
  checkIfAddressIsMatchedWithAnAddress,
  setSignatureByAddressMain,
  handleClickMpurse,
  indicateSessions,
  walletsAvailable,
  handleClickNfc,
  doSomethingWithEncryptedMnemonics,
  encryptByKeyCardStyle,
  decryptByKeyCardStyle,
  formatToKeyCardStyle,
  formatToSingleTextRecord,
  keepSubAddressesInMemory,
  getActualSubAddresses,
  keepKeyPairsAndSubAddressesInMemory,
  getParentAddressFromSubAddress,
  getSecretsFromNfc,
  getSecretsFromNfcNativeKeyCard,
  getSecretsFromQr,
};